import {
  PanelBody,
  TextControl,
  SelectControl,
  ColorPalette,
  Notice
} from "@wordpress/components";
import { InspectorControls } from "@wordpress/block-editor";
import { t } from "drupal";
import { useState, useEffect, useCallback } from "@wordpress/element";
import { handleUrlChange2, sanitizeInput, sanitizeKeepHTML, isValidDomain } from '../utilities/helpers';


function ButtonSidePanel({
  linkText = '',
  linkUrl = '',
  onChangeCTA,
  onChangeCTAUrl,
  size = '',
  ariaLabel = '',
  onChangeSize,
  backgroundColorClass = '',
  linkOnlyColorClass = '',
  linkTextError = "",
  linkUrlError = "",
  setAttributes
}) {

  const [errors, setErrors] = useState({});


  const sizeOptions = [
    { label: t("Default"), value: "btn-md" },
    { label: t("Small"), value: "btn-sm" },
    { label: t("Large"), value: "btn-lg" },
    { label: t("Link only"), value: "link" }
  ];

  const backgroundOptions = [
    { name: 'Purple', color: '#32006e', class: 'purple' },
    { name: 'Dark Grey', color: '#404040', class: 'darkgrey' },
    { name: 'Lite Grey', color: '#e5e5e5', class: 'litegrey' },
    { name: 'White', color: '#ffffff', class: 'white' },
    { name: 'Black', color: '#000000', class: 'black' },
  ];


  const onChangeAriaLabel = (value) => setAttributes({ ariaLabel: value });

  /**
   * If the user presses Enter or Tab, update the URL
   **/

    const handleKeyDown = useCallback((event) => {
      if ((event.key === 'Enter' || event.key === 'Tab') && linkUrl) {
        //handleUrlChange2 is from /deohs_gutenberg_blocks/blocks/utilities/helpers.js
        const updatedUrl = handleUrlChange2(linkUrl);
        if (updatedUrl !== linkUrl) { // Prevent unnecessary updates
          setAttributes({ linkUrl: updatedUrl });
        }
      }
    }, [linkUrl, setAttributes]);


  /* Strip out HTML and script for CTA. Limit characters to 70 because
       too many chars will appear outside the image backed card
    */
    const onChangeLinkText = (value) => {
      const cleanedText = sanitizeInput(value);
      const limitedText = cleanedText.slice(0, 70); // max 70 chars
      setAttributes({ linkText: limitedText });
    };

    // Strip out HTML and script for title
    const onChangeLink = (value) => {
      const cleanedText = sanitizeInput(value);
      setAttributes({ linkUrl: cleanedText });
    };

  /*
  * For the notice alert of side panel, it muse use useEffect instead of validation.js
  */
  useEffect(() => {
    setErrors({
      linkUrl: !isValidDomain(linkUrl),
      linkText: !linkText.trim() || linkText.trim().length > 70,
    });
  }, [linkUrl, linkText]);


  return (
    <InspectorControls>
      <PanelBody title={t("Button Settings")} initialOpen>


        {errors.linkUrl && <Notice status="error">{t("A valid button URL is required.")}</Notice>}
        <TextControl
          label={t("Card Link URL")}
          value={linkUrl}
          onChange={onChangeCTAUrl}
          className={`button-url ${errors.linkUrl ? "is-required" : ""}`} // Dynamically add class only when missing
          required
          onBlur={() => {
            //prevent running on empty string
            if (linkUrl) {
              const updatedUrl = handleUrlChange2(linkUrl);
              if (updatedUrl !== linkUrl) {
                setAttributes({ linkUrl: updatedUrl });
              }
            }
          }}
          onKeyDown={handleKeyDown} // Uses useCallback for efficiency
          placeholder={t("/about or example.com")}
        />

        {errors.linkText && <Notice status="error">{t("Button text is required.")}</Notice>}
        <TextControl
          label={t("Button text")}
          value={linkText}
          onChange={(value) => {
            onChangeCTA(value);
            setErrors((prev) => ({ ...prev, linkText: !value.trim() }));
          }}
          placeholder={t("Call to action text")}
          required
        />

        <SelectControl
          label={t("Button Style")}
          value={size}
          options={sizeOptions}
          onChange={onChangeSize}
        />

        {/* Only show background color picker if button is selected */}
        {size !== 'link' && (
          <>
            <label className="form-label">{t("Button Background Color")}</label>
            <ColorPalette
              colors={backgroundOptions}
              value={
                backgroundOptions.find(opt => opt.class === backgroundColorClass)?.color || ''
              }
              onChange={(colorHex) => {
                const selected = backgroundOptions.find(opt => opt.color === colorHex);
                if (selected) {
                  setAttributes({ backgroundColorClass: selected.class });
                }
              }}
            />
          </>
        )}

        {/* Only show linkOnly color picker if link is selected */}
        {size === 'link' && (
          <>
            <label className="form-label">{t("Link Only Color")}</label>
            <ColorPalette
              colors={backgroundOptions}
              value={
                backgroundOptions.find(opt => opt.class === linkOnlyColorClass)?.color || ''
              }
              onChange={(colorHex) => {
                const selected = backgroundOptions.find(opt => opt.color === colorHex);
                if (selected) {
                  setAttributes({ linkOnlyColorClass: selected.class });
                }
              }}
            />
          </>
        )}

      </PanelBody>

      <PanelBody title="Accessibility Settings">
        <TextControl
          label="Aria Label"
          value={ariaLabel || ""}
          onChange={onChangeAriaLabel}
          help="If you are using a generic phrase for your link text like learn more add text here so people on screen readers know where the link actually goes."
        />
      </PanelBody>
    </InspectorControls>
  );
}

export default ButtonSidePanel;
